import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { cn } from '@/shared/cn';
import {
  PROJECT_COLORS,
  PROJECT_COLOR_HEX,
  PROJECT_COLOR_LABELS,
  type ProjectColor,
} from '@/versions/v1/shared/project-colors';

export type NewProjectDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: {
    name: string;
    description: string | null;
    color: ProjectColor;
  }) => void;
};

const MAX_NAME = 80;
const MAX_DESCRIPTION = 400;
const DEFAULT_COLOR: ProjectColor = PROJECT_COLORS[0];

export function NewProjectDialog({
  open,
  onOpenChange,
  onSubmit,
}: NewProjectDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<ProjectColor>(DEFAULT_COLOR);
  const [submitting, setSubmitting] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setColor(DEFAULT_COLOR);
      setSubmitting(false);
      requestAnimationFrame(() => nameRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    onSubmit({
      name: trimmedName,
      description: description.trim() ? description.trim() : null,
      color,
    });
    onOpenChange(false);
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-zinc-900/30 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />
      <div
        className={cn(
          'relative z-10 w-[min(480px,calc(100vw-2rem))]',
          'rounded-2xl border border-[var(--glass-border)]',
          'bg-[var(--glass-surface-strong)] shadow-2xl backdrop-blur-xl',
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-project-title"
      >
        <div className="flex items-center justify-between border-b border-[var(--glass-border)] px-5 py-3.5">
          <h2
            id="new-project-title"
            className="text-lg font-normal tracking-tight text-zinc-900 dark:text-zinc-100"
          >
            New project
          </h2>
          <button
            type="button"
            aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-900/5 hover:text-zinc-900 dark:hover:bg-white/5 dark:hover:text-zinc-100"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-4 p-5">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Name
            </span>
            <input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_NAME}
              placeholder="Project name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              className="rounded-md border border-divider bg-input px-2.5 py-1.5 text-sm outline-none focus:border-input-focus disabled:opacity-50 dark:text-zinc-100"
            />
          </label>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Color
            </span>
            <div
              role="radiogroup"
              aria-label="Project color"
              className="flex flex-wrap gap-1.5"
            >
              {PROJECT_COLORS.map((c) => {
                const selected = c === color;
                return (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={PROJECT_COLOR_LABELS[c]}
                    title={PROJECT_COLOR_LABELS[c]}
                    onClick={() => setColor(c)}
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-full border transition-colors',
                      selected
                        ? 'border-zinc-900 dark:border-zinc-100'
                        : 'border-black/10 hover:border-zinc-400 dark:border-white/10',
                    )}
                    style={{ backgroundColor: PROJECT_COLOR_HEX[c] }}
                  >
                    {selected ? (
                      <Check
                        className="h-3.5 w-3.5 text-white drop-shadow-sm"
                        strokeWidth={2.4}
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Description (optional)
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={MAX_DESCRIPTION}
              rows={4}
              placeholder='e.g. "This project is about mining geology."'
              className="rounded-md border border-divider bg-input px-2.5 py-1.5 text-sm leading-relaxed outline-none focus:border-input-focus disabled:opacity-50 dark:text-zinc-100"
            />
            <span className="text-right text-xs text-zinc-400 dark:text-zinc-500">
              {description.length}/{MAX_DESCRIPTION}
            </span>
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--glass-border)] px-5 py-3.5">
          <button
            type="button"
            disabled={submitting}
            className="inline-flex h-8 items-center rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-zinc-900 px-3 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Create project
          </button>
        </div>
      </div>
    </div>
  );
}
